/**
 * Meilisearch-powered predictive search for Shopify.
 * Replaces the default Shopify predictive search with Meilisearch results.
 * Falls back to Shopify search if Meilisearch is unavailable.
 * Supports dashboard-driven design config, faceted filters, and exclusions.
 */
// Multi-language translations for search UI
const MEILI_TRANSLATIONS = {
  da: {
    no_results_for: 'Ingen resultater for',
    results: 'resultater',
    clear_filters: 'Ryd filtre',
    show_more: 'Vis {n} mere',
    from_price: 'Fra {price}',
    up_to_price: 'Op til {price}',
    price_label: 'Pris',
    apply: 'Anvend',
    remove: 'Fjern',
    reset: 'Nulstil',
    collections: 'Kollektioner',
    pages: 'Sider',
    articles: 'Artikler',
    products: 'Produkter',
    search_for: 'Søg efter',
    recommended: 'Anbefalet til dig',
    no_filter_results: 'Ingen resultater matcher dine filtre.',
    clear_all: 'Ryd alle',
  },
  en: {
    no_results_for: 'No results found for',
    results: 'results',
    clear_filters: 'Clear filters',
    show_more: 'Show {n} more',
    from_price: 'From {price}',
    up_to_price: 'Up to {price}',
    price_label: 'Price',
    apply: 'Apply',
    remove: 'Remove',
    reset: 'Reset',
    collections: 'Collections',
    pages: 'Pages',
    articles: 'Articles',
    products: 'Products',
    search_for: 'Search for',
    recommended: 'Recommended for you',
    no_filter_results: 'No results match your filters.',
    clear_all: 'Clear all',
  },
  de: {
    no_results_for: 'Keine Ergebnisse für',
    results: 'Ergebnisse',
    clear_filters: 'Filter löschen',
    show_more: '{n} mehr anzeigen',
    from_price: 'Ab {price}',
    up_to_price: 'Bis {price}',
    price_label: 'Preis',
    apply: 'Anwenden',
    remove: 'Entfernen',
    reset: 'Zurücksetzen',
    collections: 'Kollektionen',
    pages: 'Seiten',
    articles: 'Artikel',
    products: 'Produkte',
    search_for: 'Suche nach',
    recommended: 'Empfohlen für dich',
    no_filter_results: 'Keine Ergebnisse für diese Filter.',
    clear_all: 'Alle löschen',
  },
  sv: {
    no_results_for: 'Inga resultat för',
    results: 'resultat',
    clear_filters: 'Rensa filter',
    show_more: 'Visa {n} till',
    from_price: 'Från {price}',
    up_to_price: 'Upp till {price}',
    price_label: 'Pris',
    apply: 'Tillämpa',
    remove: 'Ta bort',
    reset: 'Återställ',
    collections: 'Kollektioner',
    pages: 'Sidor',
    articles: 'Artiklar',
    products: 'Produkter',
    search_for: 'Sök efter',
    recommended: 'Rekommenderat för dig',
    no_filter_results: 'Inga resultat matchar dina filter.',
    clear_all: 'Rensa alla',
  },
  no: {
    no_results_for: 'Ingen resultater for',
    results: 'resultater',
    clear_filters: 'Fjern filtre',
    show_more: 'Vis {n} mer',
    from_price: 'Fra {price}',
    up_to_price: 'Opptil {price}',
    price_label: 'Pris',
    apply: 'Bruk',
    remove: 'Fjern',
    reset: 'Nullstill',
    collections: 'Kolleksjoner',
    pages: 'Sider',
    articles: 'Artikler',
    products: 'Produkter',
    search_for: 'Søk etter',
    recommended: 'Anbefalt for deg',
    no_filter_results: 'Ingen resultater matcher filtrene dine.',
    clear_all: 'Fjern alle',
  },
  fi: {
    no_results_for: 'Ei tuloksia haulle',
    results: 'tulosta',
    clear_filters: 'Tyhjennä suodattimet',
    show_more: 'Näytä {n} lisää',
    from_price: 'Alkaen {price}',
    up_to_price: 'Enintään {price}',
    price_label: 'Hinta',
    apply: 'Käytä',
    remove: 'Poista',
    reset: 'Nollaa',
    collections: 'Kokoelmat',
    pages: 'Sivut',
    articles: 'Artikkelit',
    products: 'Tuotteet',
    search_for: 'Hae',
    recommended: 'Suositellut sinulle',
    no_filter_results: 'Suodattimilla ei löytynyt tuloksia.',
    clear_all: 'Tyhjennä kaikki',
  }
};

class MeilisearchSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Meilisearch config from data attributes
    this.meiliHost = this.dataset.meiliHost || '';
    this.meiliKey = this.dataset.meiliKey || '';
    this.meiliEnabled = !!(this.meiliHost && this.meiliKey);
    this.dashboardUrl = this.dataset.dashboardUrl || '';
    this.locale = (this.dataset.locale || 'da').toLowerCase();
    this.localeRoot = this.dataset.localeRoot || '/';

    // Dynamic config (loaded from dashboard)
    this.searchConfig = null;
    this.filtersConfig = null;
    this.excludedProductIds = [];
    this.dashboardTranslations = null;
    this.colorSwatches = {};
    this.configLoaded = false;

    // Active user-selected filters
    this.activeFilters = {};
    this.priceRange = { min: null, max: null };
    this._filterInteraction = false;

    this.setupEventListeners();
    this.setupFullOnOpen();
    this.preloadForFullOnOpen();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Prevent close when clicking inside the results/filter area
    this.predictiveSearchResults.addEventListener('mousedown', () => {
      this._resultsInteraction = true;
    });
    document.addEventListener('mouseup', () => {
      this._resultsInteraction = false;
    });
  }

  setupFullOnOpen() {
    const detailsEl = this.closest('details');
    const searchWrap = !detailsEl ? this.closest('.template-search__search') : null;
    const observeEl = detailsEl || searchWrap;
    if (!observeEl) return;

    const isOpen = () => detailsEl
      ? detailsEl.hasAttribute('open')
      : !searchWrap.classList.contains('hidden');

    const observer = new MutationObserver(() => {
      if (isOpen() && !this._fullOnOpenTriggered) {
        this._fullOnOpenTriggered = true;
        if (!this.getQuery().length) {
          // Content is already pre-injected and CSS shows it instantly.
          // Just sync JS state.
          this.isOpen = true;
          this.setAttribute('open', true);
          this.setAttribute('results', true);
          this.input.setAttribute('aria-expanded', true);
          if (!this._preloadedDefaultHTML && !this.classList.contains('meili-full-ready')) {
            // Preload hasn't started/finished — fallback to live fetch
            this.renderSearchResults(this.buildSkeletonHTML());
            this.showDefaultProducts();
          }
        }
      } else if (!isOpen()) {
        this._fullOnOpenTriggered = false;
      }
    });

    if (detailsEl) {
      observer.observe(detailsEl, { attributes: true, attributeFilter: ['open'] });
    } else {
      observer.observe(searchWrap, { attributes: true, attributeFilter: ['class'] });
    }
  }

  _isFullOnOpenActive() {
    if (!this.classList.contains('meili-full-ready')) return false;
    const sw = this.closest('.template-search__search');
    return !!(this.closest('details')?.hasAttribute('open') || (sw && !sw.classList.contains('hidden')));
  }

  buildSkeletonHTML() {
    const cols = this.getCfg('gridColumns', 4) || 4;
    const count = this.getCfg('maxResults', 8) || 8;
    const items = Array.from({ length: Math.min(count, 8) }, () =>
      `<li style="grid-column:span ${Math.floor(12 / cols)};background:#f9fafb;border-radius:8px;overflow:hidden">
        <div style="height:200px;background:linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 50%,#f3f4f6 75%);background-size:200% 100%;animation:shimmer 1.5s infinite"></div>
        <div style="padding:8px"><div style="height:12px;width:70%;background:#e5e7eb;border-radius:4px;margin-bottom:6px"></div><div style="height:12px;width:40%;background:#e5e7eb;border-radius:4px"></div></div>
      </li>`
    ).join('');
    return `
      <style>@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}</style>
      <div id="predictive-search-results" class="w-full" style="background:#fff" role="listbox">
        <div style="background:#fff">
          <div style="padding:16px 16px 8px;height:14px;width:160px;background:#e5e7eb;border-radius:4px"></div>
          <ul class="list-unstyled" style="display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:8px;padding:12px 16px 16px">${items}</ul>
        </div>
      </div>`;
  }

  async preloadForFullOnOpen() {
    try {
      await this.loadSearchConfig();
      if (!this.getCfg('showFullOnOpen', false)) return;
      // Pre-inject skeleton so CSS can show it instantly when details opens
      this.predictiveSearchResults.innerHTML = this.buildSkeletonHTML();
      this.classList.add('meili-full-ready');
      // Load real content in background
      const html = await this.buildDefaultProductsPreload();
      if (html) {
        this._preloadedDefaultHTML = html;
        this.predictiveSearchResults.innerHTML = html;
        this._initBundleSliders();
        // If modal/search already open (user clicked before preload finished), update state
        const sw = this.closest('.template-search__search');
        if (this.closest('details')?.hasAttribute('open') || (sw && !sw.classList.contains('hidden'))) {
          this.setAttribute('results', true);
          this.open();
        }
      } else {
        // Preload returned no content — remove ready class so fallbacks can trigger
        this.classList.remove('meili-full-ready');
        this.predictiveSearchResults.innerHTML = '';
      }
    } catch (e) {
      // Preload failed — remove ready class so fallbacks can trigger
      this.classList.remove('meili-full-ready');
      this.predictiveSearchResults.innerHTML = '';
    }
  }

  // Shared helper: build exclusion filter for default product queries
  _buildExclusionFilter() {
    const parts = [];
    // Always exclude sold-out products server-side
    parts.push('(available = true OR resource_type != "product")');
    if (this.excludedProductIds.length > 0) {
      parts.push(`id NOT IN [${this.excludedProductIds.join(', ')}]`);
    }
    return parts.length > 0 ? parts.join(' AND ') : null;
  }

  // Shared helper: combine filters with AND
  _combineFilters(...filters) {
    const valid = filters.filter(Boolean);
    return valid.length > 0 ? valid.join(' AND ') : undefined;
  }

  // Shared Meilisearch search with automatic facet-error retry
  async _meiliSearch(searchBody, signal) {
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.meiliKey}` };
    const doFetch = (body) => fetch(`${this.meiliHost}/indexes/products/search`, {
      method: 'POST', headers, body: JSON.stringify(body), signal
    });
    let resp = await doFetch(searchBody);
    if (!resp.ok && searchBody.facets && searchBody.facets.length > 0) {
      try {
        const err = await resp.json();
        const bad = (err.message || '').match(/Attribute `([^`]+)` is not filterable/)?.[1];
        if (bad) {
          searchBody.facets = searchBody.facets.filter(f => f !== bad);
          if (searchBody.facets.length === 0) delete searchBody.facets;
        } else { delete searchBody.facets; }
      } catch (e) { delete searchBody.facets; }
      resp = await doFetch(searchBody);
    }
    if (!resp.ok) throw new Error('Meilisearch error');
    return resp.json();
  }

  // Shared helper: apply display rules (hideNoImage, hideOutOfStock, hideZeroPrice, customRules)
  _applyDisplayRules(hits) {
    const isProduct = h => !h.resource_type || h.resource_type === 'product';
    if (this.getCfg('hideNoImage', false)) hits = hits.filter(h => !isProduct(h) || h.image);
    // Always hide sold-out products
    hits = hits.filter(h => !isProduct(h) || h.available !== false);
    if (this.getCfg('hideZeroPrice', false)) hits = hits.filter(h => !isProduct(h) || (h.price && h.price > 0));

    const customRules = this.getCfg('customRules', []);
    if (customRules.length > 0) {
      const matchesRule = (h, rule) => {
        const val = h[rule.attribute];
        const ruleVal = rule.value || '';
        switch (rule.operator) {
          case 'contains':
            return Array.isArray(val) ? val.some(v => String(v).toLowerCase().includes(ruleVal.toLowerCase())) : String(val || '').toLowerCase().includes(ruleVal.toLowerCase());
          case 'not_contains':
            return Array.isArray(val) ? !val.some(v => String(v).toLowerCase().includes(ruleVal.toLowerCase())) : !String(val || '').toLowerCase().includes(ruleVal.toLowerCase());
          case 'equals':
            return Array.isArray(val) ? val.includes(ruleVal) : String(val || '') === ruleVal;
          case 'not_equals':
            return Array.isArray(val) ? !val.includes(ruleVal) : String(val || '') !== ruleVal;
          case 'is_empty':
            return !val || (Array.isArray(val) && val.length === 0) || val === '';
          case 'is_not_empty':
            return val && (!Array.isArray(val) || val.length > 0) && val !== '';
          default:
            return false;
        }
      };
      const hideRules = customRules.filter(r => r.action !== 'show_only');
      const showOnlyRules = customRules.filter(r => r.action === 'show_only');
      hits = hits.filter(h => {
        for (const rule of hideRules) {
          if (matchesRule(h, rule)) return false;
        }
        if (showOnlyRules.length > 0 && !showOnlyRules.some(rule => matchesRule(h, rule))) return false;
        return true;
      });
    }
    return hits;
  }

  // Shared helper: fetch hits from a single source type
  async _fetchSourceHits(sourceType, dp, maxItems, facetsParam) {
    const headers = { 'Authorization': `Bearer ${this.meiliKey}`, 'Content-Type': 'application/json' };
    const exclusionFilter = this._buildExclusionFilter();

    // Include custom rule attributes so client-side display rules can evaluate them
    const ruleAttrs = this.getCfg('customRules', []).map(r => r.attribute).filter(Boolean);
    const extraAttrs = ruleAttrs.length > 0 ? [...new Set(ruleAttrs)] : [];

    // Over-fetch to compensate for client-side filtering
    const hasClientFilters = true; // Always over-fetch since sold-out products are always filtered
    const fetchLimit = hasClientFilters ? maxItems * 3 : maxItems;

    // Safe fetch that never throws — returns empty result on error
    const safeFetch = async (searchBody) => {
      try {
        const resp = await fetch(`${this.meiliHost}/indexes/products/search`, { method: 'POST', headers, body: JSON.stringify(searchBody) });
        if (!resp.ok) {
          // If facets caused the error, retry without the bad one
          if (searchBody.facets && searchBody.facets.length > 0) {
            try {
              const err = await resp.json();
              const bad = (err.message || '').match(/Attribute `([^`]+)` is not filterable/)?.[1];
              if (bad) searchBody.facets = searchBody.facets.filter(f => f !== bad);
              else delete searchBody.facets;
            } catch (e) { delete searchBody.facets; }
            if (searchBody.facets && searchBody.facets.length === 0) delete searchBody.facets;
            const retry = await fetch(`${this.meiliHost}/indexes/products/search`, { method: 'POST', headers, body: JSON.stringify(searchBody) });
            if (retry.ok) return retry.json();
          }
          return { hits: [], facetDistribution: null, facetStats: null };
        }
        return resp.json();
      } catch (e) {
        return { hits: [], facetDistribution: null, facetStats: null };
      }
    };

    if (sourceType === 'recently_viewed') {
      const viewed = JSON.parse(localStorage.getItem('smelt_recently_viewed') || '[]');
      if (viewed.length === 0) return { hits: [], facetDistribution: null, facetStats: null };
      const handleFilter = viewed.slice(0, fetchLimit).map(h => `handle = "${h}"`).join(' OR ');
      const searchBody = { q: '', limit: fetchLimit, filter: this._combineFilters(`(${handleFilter})`, exclusionFilter) };
      if (facetsParam) searchBody.facets = [...facetsParam];
      if (extraAttrs.length > 0) searchBody.attributesToRetrieve = ['*'];
      const data = await safeFetch(searchBody);
      const hits = data.hits || [];
      const orderMap = {}; viewed.forEach((h, i) => { orderMap[h] = i; });
      hits.sort((a, b) => (orderMap[a.handle] ?? 999) - (orderMap[b.handle] ?? 999));
      return { hits, facetDistribution: data.facetDistribution || null, facetStats: data.facetStats || null };
    } else if (sourceType === 'manual') {
      const handles = dp.handles || [];
      if (handles.length === 0) return { hits: [], facetDistribution: null, facetStats: null };
      const handleFilter = handles.map(h => `handle = "${h}"`).join(' OR ');
      const searchBody = { q: '', limit: handles.length, filter: this._combineFilters(`(${handleFilter})`, exclusionFilter) };
      if (facetsParam) searchBody.facets = [...facetsParam];
      if (extraAttrs.length > 0) searchBody.attributesToRetrieve = ['*'];
      const data = await safeFetch(searchBody);
      const hits = data.hits || [];
      const orderMap = {}; handles.forEach((h, i) => { orderMap[h] = i; });
      hits.sort((a, b) => (orderMap[a.handle] ?? 999) - (orderMap[b.handle] ?? 999));
      return { hits, facetDistribution: data.facetDistribution || null, facetStats: data.facetStats || null };
    } else {
      const searchBody = { q: '', limit: fetchLimit, filter: this._combineFilters('resource_type = "product"', exclusionFilter) };
      if (facetsParam) searchBody.facets = [...facetsParam];
      if (extraAttrs.length > 0) searchBody.attributesToRetrieve = ['*'];
      const data = await safeFetch(searchBody);
      return { hits: data.hits || [], facetDistribution: data.facetDistribution || null, facetStats: data.facetStats || null };
    }
  }

  // Shared helper: fetch combined hits from multiple sources
  async _fetchCombinedDefaultProducts(dp) {
    const maxItems = dp.maxItems || 8;
    const enabledFacets = this.getEnabledFacets();
    const facetsParam = enabledFacets.length > 0 ? enabledFacets : undefined;

    // Build sources list: new combined format or legacy single source with fallbacks
    let sources;
    if (dp.sources && Array.isArray(dp.sources) && dp.sources.length > 0) {
      sources = dp.sources.filter(s => s.enabled);
    } else {
      // Legacy single source — build fallback chain so empty sources cascade
      const primary = dp.source || 'popular';
      sources = [{ type: primary, enabled: true }];
      if (primary === 'recently_viewed' && dp.handles?.length > 0) {
        sources.push({ type: 'manual', enabled: true });
      }
      if (primary !== 'popular') {
        sources.push({ type: 'popular', enabled: true });
      }
    }

    let allHits = [];
    const seenHandles = new Set();
    let facetDistribution = null;
    let facetStats = null;

    for (const src of sources) {
      if (allHits.length >= maxItems) break;
      const result = await this._fetchSourceHits(src.type, dp, maxItems, facetsParam);
      // Use facets from the first source that has them
      if (!facetDistribution && result.facetDistribution) {
        facetDistribution = result.facetDistribution;
        facetStats = result.facetStats;
      }
      // Apply display rules before dedup so filtered products don't count toward limit
      const filtered = this._applyDisplayRules(result.hits);
      for (const hit of filtered) {
        if (!seenHandles.has(hit.handle)) {
          seenHandles.add(hit.handle);
          allHits.push(hit);
          if (allHits.length >= maxItems) break;
        }
      }
    }

    return { hits: allHits.slice(0, maxItems), facetDistribution, facetStats };
  }

  async buildDefaultProductsPreload() {
    const dp = this._resolveDefaultProductsConfig();
    if (!dp) return null;

    const { hits, facetDistribution, facetStats } = await this._fetchCombinedDefaultProducts(dp);

    if (hits.length === 0) return null;
    const snippetCards = await this.fetchSnippetCards(hits.filter(h => !h.resource_type || h.resource_type === 'product').map(h => h.handle));
    return this.buildDefaultProductsHTML(hits, dp.title, snippetCards, facetDistribution, facetStats);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Use preloaded HTML if available to avoid slow re-fetch
      if (this._preloadedDefaultHTML) {
        this.renderSearchResults(this._preloadedDefaultHTML);
      } else {
        this.showDefaultProducts();
      }
      return;
    }

    // Clear cached results when filters are active (filter combos make caching unreliable)
    if (Object.keys(this.activeFilters).length > 0 || this.priceRange.min !== null || this.priceRange.max !== null) {
      this.cachedResults = {};
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      // If preloaded content is already showing, don't re-fetch
      if (this._fullOnOpenTriggered && (this.isOpen || this.classList.contains('meili-full-ready'))) return;
      // Detect full-on-open before MutationObserver fires (race condition fix)
      if (this._isFullOnOpenActive()) {
        this._fullOnOpenTriggered = true;
        this.isOpen = true;
        this.setAttribute('open', true);
        this.setAttribute('results', true);
        this.input.setAttribute('aria-expanded', true);
        if (!this._preloadedDefaultHTML && !this.classList.contains('meili-full-ready')) {
          this.renderSearchResults(this.buildSkeletonHTML());
          this.showDefaultProducts();
        }
        return;
      }
      this.showDefaultProducts();
      return;
    }
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    // If focus is moving to another element inside this component, don't close
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    setTimeout(() => {
      if (this._filterInteraction || this._resultsInteraction) return;
      // In full-on-open mode, keep results visible while modal/search is open
      if (this.classList.contains('meili-full-ready')) {
        const sw = this.closest('.template-search__search');
        if (this.closest('details')?.hasAttribute('open') || (sw && !sw.classList.contains('hidden'))) return;
      }
      if (!this.contains(document.activeElement)) this.close();
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (this._isFullOnOpenActive()) {
        this.showDefaultProducts();
      } else {
        this.close(true);
      }
    }
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }
    this.statusElement.textContent = '';
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  // ---- Config loading ----

  async loadSearchConfig() {
    if (this.configLoaded) return;
    if (!this.dashboardUrl) { this.configLoaded = true; return; }

    // Check sessionStorage cache (5-min TTL)
    let loaded = false;
    try {
      const cached = sessionStorage.getItem('meili_search_config');
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 5 * 60 * 1000) {
          this.searchConfig = data.config;
          this.filtersConfig = data.filters;
          this.excludedProductIds = data.excludedProductIds || [];
          this.dashboardTranslations = data.translations || null;
          this.colorSwatches = data.colorSwatches || {};
          loaded = true;
        }
      }
    } catch (e) { /* ignore cache errors */ }

    if (!loaded) {
      try {
        const resp = await fetch(`${this.dashboardUrl}/api/meilisearch/search-config`);
        if (!resp.ok) throw new Error('Config fetch failed');
        const data = await resp.json();
        this.searchConfig = data.config;
        this.filtersConfig = data.filters;
        this.excludedProductIds = data.excludedProductIds || [];
        this.dashboardTranslations = data.translations || null;
        this.colorSwatches = data.colorSwatches || {};
        sessionStorage.setItem('meili_search_config', JSON.stringify({ data, ts: Date.now() }));
      } catch (e) {
        console.warn('[Meilisearch] Could not load search config:', e.message);
      }
    }
    this.configLoaded = true;
    this._applyBackdropStyle();
    this._applySearchInputStyles();
  }

  _applyBackdropStyle() {
    const s = this.getCfg('styles', null);
    if (!s?.backdropColor) return;
    const overlay = document.getElementById('search-overlay');
    if (!overlay) return;
    const opacity = (s.backdropOpacity ?? 40) / 100;
    const hex = s.backdropColor;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    overlay.style.setProperty('--search-overlay-bg', `rgba(${r},${g},${b},${opacity})`);
  }

  _applySearchInputStyles() {
    const s = this.getCfg('styles', null);
    if (!s) return;
    const bg = s.searchInputBg;
    const borderColor = s.searchInputBorderColor;
    const textColor = s.searchInputTextColor;
    if (!bg && !borderColor && !textColor) return;
    const form = this.querySelector('form.search');
    if (!form) return;
    // Apply border/bg to the .field wrapper (not the form, which also contains the results dropdown)
    const field = form.querySelector('.field') || form;
    if (bg) field.style.background = bg;
    if (borderColor) {
      field.style.borderTop = `1px solid ${borderColor}`;
      field.style.borderBottom = `1px solid ${borderColor}`;
    }
    const input = form.querySelector('.search__input');
    if (input) {
      if (bg) input.style.background = bg;
      if (textColor) input.style.color = textColor;
    }
  }

  getCfg(key, defaultVal) {
    if (this.searchConfig && this.searchConfig[key] !== undefined) return this.searchConfig[key];
    return defaultVal;
  }

  getStyleVars() {
    const s = this.getCfg('styles', null);
    if (!s) return '';
    const vars = [];
    if (s.overlayBg) vars.push(`--ms-overlay-bg:${s.overlayBg}`);
    if (s.containerBg) vars.push(`--ms-container-bg:${s.containerBg}`);
    if (s.cardBg) vars.push(`--ms-card-bg:${s.cardBg}`);
    if (s.textColor) vars.push(`--ms-text:${s.textColor}`);
    if (s.secondaryTextColor) vars.push(`--ms-text-secondary:${s.secondaryTextColor}`);
    if (s.accentColor) vars.push(`--ms-accent:${s.accentColor}`);
    if (s.priceColor) vars.push(`--ms-price:${s.priceColor}`);
    if (s.comparePriceColor) vars.push(`--ms-compare-price:${s.comparePriceColor}`);
    if (s.saleBadgeColor) vars.push(`--ms-sale-badge:${s.saleBadgeColor}`);
    if (s.cardBorderColor) vars.push(`--ms-card-border:${s.cardBorderColor}`);
    if (s.cardBorderRadius) vars.push(`--ms-card-radius:${s.cardBorderRadius}`);
    return vars.join(';');
  }

  getHoverEffect() {
    const s = this.getCfg('styles', null);
    return s?.cardHoverEffect || 'shadow';
  }

  // ---- Filter string building ----

  buildFilterString() {
    const parts = [];

    // Always exclude sold-out products server-side
    parts.push('(available = true OR resource_type != "product")');

    // Resource type filter
    const resourceTypes = [];
    if (this.getCfg('showProducts', true)) resourceTypes.push('product');
    if (this.getCfg('showCollections', false)) resourceTypes.push('collection');
    if (this.getCfg('showPages', false)) resourceTypes.push('page');
    if (this.getCfg('showArticles', false)) resourceTypes.push('article');
    if (resourceTypes.length > 0 && resourceTypes.length < 4) {
      const rtConditions = resourceTypes.map(t => `resource_type = "${t}"`);
      parts.push(rtConditions.length === 1 ? rtConditions[0] : `(${rtConditions.join(' OR ')})`);
    }

    // Exclusions
    if (this.excludedProductIds.length > 0) {
      parts.push(`id NOT IN [${this.excludedProductIds.join(', ')}]`);
    }

    // Checkbox facets
    for (const [attr, values] of Object.entries(this.activeFilters)) {
      if (values.length > 0) {
        if (attr === 'available') {
          parts.push(values.map(v => `available = ${v}`).join(' OR '));
        } else {
          const conditions = values.map(v => `${attr} = "${v.replace(/"/g, '\\"')}"`);
          parts.push(conditions.length === 1 ? conditions[0] : `(${conditions.join(' OR ')})`);
        }
      }
    }

    // Price range
    if (this.priceRange.min !== null) parts.push(`price >= ${this.priceRange.min}`);
    if (this.priceRange.max !== null) parts.push(`price <= ${this.priceRange.max}`);

    return parts.length > 0 ? parts.join(' AND ') : undefined;
  }

  getEnabledFacets() {
    if (!this.filtersConfig || !this.filtersConfig.enabled) return [];
    return (this.filtersConfig.facets || []).filter(f => f.enabled).map(f => f.attribute);
  }

  // ---- Search ----

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.meiliEnabled) {
      this.getMeilisearchResults(searchTerm, queryKey);
    } else {
      this.getShopifyResults(searchTerm, queryKey);
    }
  }

  async getMeilisearchResults(searchTerm, queryKey) {
    try {
      await this.loadSearchConfig();

      const maxResults = this.getCfg('maxResults', 8);
      const enabledFacets = this.getEnabledFacets();
      const filter = this.buildFilterString();

      const baseAttrs = ['id', 'title', 'handle', 'image', 'price', 'compare_at_price', 'vendor', 'product_type', 'url', 'available', 'resource_type', 'body'];
      // Include any attributes referenced by custom rules so client-side filtering works
      const ruleAttrs = this.getCfg('customRules', []).map(r => r.attribute).filter(a => a && !baseAttrs.includes(a));
      const attributesToRetrieve = ruleAttrs.length > 0 ? [...baseAttrs, ...new Set(ruleAttrs)] : baseAttrs;

      // Over-fetch to compensate for client-side filtering, then slice to maxResults
      const hasClientFilters = true; // Always over-fetch since sold-out products are always filtered
      const searchBody = {
        q: searchTerm,
        limit: hasClientFilters ? maxResults * 3 : maxResults,
        attributesToRetrieve
      };

      if (filter) searchBody.filter = filter;
      if (enabledFacets.length > 0) searchBody.facets = enabledFacets;

      // Restrict search to current language fields (always include base fields as fallback)
      if (this.locale && this.locale !== 'en') {
        searchBody.attributesToSearchOn = [
          'title', 'title_suffixes', 'body',
          `title_${this.locale}`, `title_suffixes_${this.locale}`, `body_${this.locale}`,
          'vendor', 'product_type', 'tags'
        ];
      }

      const data = await this._meiliSearch(searchBody, this.abortController.signal);
      let hits = this._applyDisplayRules(data.hits || []);

      // Trim to maxResults after client-side filtering
      if (hits.length > maxResults) hits = hits.slice(0, maxResults);

      // Pre-fetch snippet-rendered cards if configured
      const productHandles = hits.filter(h => !h.resource_type || h.resource_type === 'product').map(h => h.handle);
      const snippetCards = await this.fetchSnippetCards(productHandles);

      const resultsMarkup = this.buildMeilisearchResultsHTML(
        hits,
        searchTerm,
        data.estimatedTotalHits || 0,
        data.facetDistribution || null,
        data.facetStats || null,
        snippetCards
      );

      // Only cache if no active filters
      if (Object.keys(this.activeFilters).length === 0 && this.priceRange.min === null && this.priceRange.max === null) {
        this.allPredictiveSearchInstances.forEach(inst => {
          inst.cachedResults[queryKey] = resultsMarkup;
        });
      }
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) return; // Aborted
      console.warn('[Meilisearch] Falling back to Shopify search:', error.message);
      this.getShopifyResults(searchTerm, queryKey);
    }
  }

  getShopifyResults(searchTerm, queryKey) {
    fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then(response => {
        if (!response.ok) { this.close(); throw new Error(response.status); }
        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach(inst => {
          inst.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch(error => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  // ---- Snippet card fetching (Section Rendering API) ----

  async fetchSnippetCards(handles) {
    const cardSnippet = this.getCfg('cardSnippet', '');
    if (!cardSnippet || handles.length === 0) return {};

    const sectionId = 'meili-card-render';
    const localePrefix = this.localeRoot.endsWith('/') ? this.localeRoot.slice(0, -1) : this.localeRoot;
    const cacheKey = `meili_snippet_cards_${this.locale}`;
    const cacheTTL = 5 * 60 * 1000; // 5 minutes
    let cache = {};

    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.ts && Date.now() - parsed.ts < cacheTTL) cache = parsed.cards || {};
      }
    } catch (e) { /* ignore */ }

    const uncached = handles.filter(h => !cache[h]);

    if (uncached.length > 0) {
      const fetches = uncached.map(async (handle) => {
        try {
          const resp = await fetch(`${localePrefix}/products/${encodeURIComponent(handle)}?section_id=${sectionId}`);
          if (!resp.ok) return;
          const html = await resp.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('.meili-rendered-card');
          if (card && card.dataset.available !== 'false') cache[handle] = card.innerHTML;
        } catch (e) { /* skip failed fetches */ }
      });
      await Promise.all(fetches);

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), cards: cache }));
      } catch (e) { /* storage full */ }
    }

    return cache;
  }

  // ---- Results rendering ----

  buildMeilisearchResultsHTML(hits, searchTerm, totalHits, facetDistribution, facetStats, snippetCards, options = {}) {
    if (hits.length === 0 && Object.keys(this.activeFilters).length === 0 && this.priceRange.min === null) {
      return `
        <div style="padding:24px;text-align:center;color:#6b7280">
          <p>${this.t('no_results_for')} "<strong>${this.escapeHtml(searchTerm)}</strong>"</p>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>0 ${this.t('results')}</span>
      `;
    }

    const showTitle = this.getCfg('showTitle', true);
    const showPrice = this.getCfg('showPrice', window.meiliShowPrice !== false);
    const showCompareAtPrice = this.getCfg('showCompareAtPrice', true);
    const showVendor = this.getCfg('showVendor', window.meiliShowVendor !== false);
    const showSearchHeader = this.getCfg('showSearchHeader', true);
    const showResultCount = this.getCfg('showResultCount', true);
    const gridColumns = this.getCfg('gridColumns', 4);
    const imageHeight = this.getCfg('imageHeight', 'medium');
    const filtersEnabled = this.filtersConfig && this.filtersConfig.enabled && facetDistribution;
    const styleVars = this.getStyleVars();
    const hoverEffect = this.getHoverEffect();

    const imgHeightPx = imageHeight === 'small' ? 160 : imageHeight === 'large' ? 288 : 224;
    const colSpan = Math.floor(12 / gridColumns);

    // Read per-section styles from layout config
    const layoutCfg = this.getCfg('layout', null);
    const sectionStyles = {};
    if (layoutCfg && Array.isArray(layoutCfg)) {
      for (const row of layoutCfg) {
        for (const sec of (row.sections || [])) {
          if (sec.styles) sectionStyles[sec.id] = sec.styles;
        }
      }
    }
    const productsGap = sectionStyles.products?.gap || '0';

    // Group hits by resource_type
    const products = hits.filter(h => !h.resource_type || h.resource_type === 'product');
    const collections = hits.filter(h => h.resource_type === 'collection');
    const pages = hits.filter(h => h.resource_type === 'page');
    const articles = hits.filter(h => h.resource_type === 'article');

    // Build product grid items — when gap is set, use simple N-column grid (no spans)
    const hasProductsGap = parseInt(productsGap) > 0;
    const useSnippets = snippetCards && Object.keys(snippetCards).length > 0;
    const productItems = products.map((hit, index) => {
      const itemColStyle = hasProductsGap ? '' : `grid-column:span ${colSpan};`;

      // Use snippet-rendered card if available
      if (useSnippets && snippetCards[hit.handle]) {
        return `<li class="predictive-search__list-item" style="${itemColStyle}${hasProductsGap ? '' : 'margin-bottom:8px;'}" role="option" aria-selected="false">${snippetCards[hit.handle]}</li>`;
      }

      const priceFormatted = hit.price ? this.formatPrice(hit.price) : '';
      const compareFormatted = showCompareAtPrice && hit.compare_at_price && hit.compare_at_price > hit.price ? this.formatPrice(hit.compare_at_price) : '';
      const imageHtml = hit.image
        ? `<div style="width:100%;overflow:hidden"><img class="predictive-search__image" style="object-fit:cover;width:100%;height:${imgHeightPx}px" src="${this.escapeHtml(hit.image)}" alt="${this.escapeHtml(hit.title)}" width="70" height="105" loading="lazy"></div>`
        : '';

      const hoverAttr = hoverEffect === 'scale' ? 'onmouseenter="this.style.transform=\'scale(1.03)\'" onmouseleave="this.style.transform=\'scale(1)\'"'
        : '';

      return `
        <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" style="${itemColStyle}${hasProductsGap ? '' : 'margin-bottom:8px;'}background:var(--ms-card-bg,#fff);border:1px solid var(--ms-card-border,transparent);border-radius:var(--ms-card-radius,8px);overflow:hidden;transition:transform .15s,box-shadow .15s" ${hoverAttr} role="option" aria-selected="false">
          <a href="${this.escapeHtml(hit.url || `/products/${hit.handle}`)}" class="predictive-search__item predictive-search__item--link-with-thumbnail" tabindex="-1" style="display:flex;flex-direction:column;row-gap:8px;color:var(--ms-text,inherit);text-decoration:none">
            ${imageHtml}
            <div class="predictive-search__item-content" style="padding:0 4px 4px">
              ${showVendor && hit.vendor ? `<div class="predictive-search__item-vendor caption-with-letter-spacing" style="color:var(--ms-text-secondary,#6b7280)">${this.escapeHtml(hit.vendor)}</div>` : ''}
              ${showTitle ? `<p class="predictive-search__item-heading h5" style="font-size:0.85rem;line-height:1.2;color:var(--ms-text,inherit)">${this.escapeHtml(hit.title)}</p>` : ''}
              ${showPrice && priceFormatted ? `
                <div class="price">
                  ${compareFormatted ? `<s class="price-item price-item--regular" style="font-size:0.875rem;color:var(--ms-compare-price,#9ca3af)">${compareFormatted}</s>` : ''}
                  <span class="price-item price-item--sale" style="font-weight:500;color:var(--ms-price,inherit)">${priceFormatted}</span>
                </div>
              ` : ''}
            </div>
          </a>
        </li>
      `;
    }).join('');

    // Build list items for collections, pages, articles
    const buildListSection = (items, label, icon) => {
      if (items.length === 0) return '';
      const listItems = items.map(hit => {
        const excerpt = hit.body ? this.escapeHtml(hit.body.substring(0, 100)) + (hit.body.length > 100 ? '...' : '') : '';
        const imageThumb = hit.image ? `<img src="${this.escapeHtml(hit.image)}" alt="" style="width:40px;height:40px;object-fit:cover;border-radius:4px;flex-shrink:0">` : `<div style="width:40px;height:40px;border-radius:4px;background:#f3f4f6;display:flex;align-items:center;justify-content:center;flex-shrink:0">${icon}</div>`;
        return `
          <li class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${this.escapeHtml(hit.url)}" class="meili-list-link" style="display:flex;align-items:center;gap:12px;padding:10px 16px;transition:background .15s;text-decoration:none;color:inherit" tabindex="-1">
              ${imageThumb}
              <div style="min-width:0;flex:1">
                <p style="font-size:0.875rem;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin:0;color:var(--ms-text,#111827)">${this.escapeHtml(hit.title)}</p>
                ${excerpt ? `<p style="font-size:0.75rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin:2px 0 0;color:var(--ms-text-secondary,#6b7280)">${excerpt}</p>` : ''}
              </div>
              <svg style="width:16px;height:16px;color:#9ca3af;flex-shrink:0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </a>
          </li>`;
      }).join('');
      return `
        <div style="border-top:1px solid var(--ms-card-border,#f3f4f6)">
          <h3 style="padding:12px 16px 4px;font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;margin:0;color:var(--ms-text-secondary,#6b7280)">${label}</h3>
          <ul style="list-style:none;margin:0;padding:0">${listItems}</ul>
        </div>`;
    };

    const collectionsHtml = buildListSection(collections, this.t('collections'), '<svg style="width:20px;height:20px;color:#9ca3af" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>');
    const pagesHtml = buildListSection(pages, this.t('pages'), '<svg style="width:20px;height:20px;color:#9ca3af" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>');
    const articlesHtml = buildListSection(articles, this.t('articles'), '<svg style="width:20px;height:20px;color:#9ca3af" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/></svg>');

    // Filter sidebar
    const filterSidebarHtml = filtersEnabled ? this.buildFilterSidebarHTML(facetDistribution, facetStats) : '';

    const resultsColSpan = filtersEnabled ? 'grid-column:span 9' : 'grid-column:span 12';
    const filterColSpan = 'grid-column:span 3';
    const mobileFilterStyle = 'display:none';

    const headerHtml = options.headerHtml !== undefined ? options.headerHtml : (showSearchHeader ? `
      <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button predictive-search__heading" style="padding:16px 24px">
        <button class="predictive-search__item" tabindex="-1" role="option" aria-selected="false">
          <span class="Heading" data-predictive-search-search-for-text>
            ${this.t('search_for')} "${this.escapeHtml(searchTerm)}"${showResultCount ? ` (${totalHits} ${this.t('results')})` : ''}
          </span>
        </button>
      </div>
    ` : '');

    const noResultsHtml = hits.length === 0 ? `
      <div style="padding:24px;text-align:center;color:#6b7280">
        <p>${this.t('no_filter_results')} <button type="button" onclick="document.querySelector('predictive-search').clearAllFilters()" style="color:#1a1a1a;text-decoration:underline;background:none;border:none;cursor:pointer">${this.t('clear_filters')}</button></p>
      </div>
    ` : '';

    // Build section content map
    const hasOtherTypes = collections.length > 0 || pages.length > 0 || articles.length > 0;
    const productsHeaderHtml = hasOtherTypes && products.length > 0 ? `<h3 style="padding:12px 16px 4px;font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;margin:0;color:var(--ms-text-secondary,#6b7280)">${this.t('products')}</h3>` : '';

    const sectionMap = {
      search_header: headerHtml,
      filters: filterSidebarHtml ? `<div style="border-right:1px solid #e5e7eb;padding:16px;overflow-y:auto;max-height:70vh">${filterSidebarHtml}</div>` : '',
      products: products.length > 0 ? `${options.productsTitle || ''}${productsHeaderHtml}<ul id="predictive-search-results-products-list" class="predictive-search__results-list" style="display:grid;grid-template-columns:repeat(${hasProductsGap ? gridColumns : 12},minmax(0,1fr));gap:${productsGap}px;list-style:none;margin:0;padding:0" role="group">${productItems}</ul>` : '',
      collections: collectionsHtml,
      pages: pagesHtml,
      articles: articlesHtml
    };

    // Build content using layout rows (new) or sections (legacy) or default
    const layout = this.getCfg('layout', null);
    let contentHtml = '';
    let layoutHasFilters = false;

    if (layout && Array.isArray(layout) && layout.length > 0) {
      layoutHasFilters = layout.some(r => r.sections?.some(s => s.id === 'filters'));
      // Row/column layout
      contentHtml = layout.map(row => {
        if (!row.sections || row.sections.length === 0) return '';
        const cells = row.sections.map(sec => {
          const html = sectionMap[sec.id] || '';
          if (!html) return '';
          const ss = sec.styles || {};
          const cellStyles = [
            `grid-column:span ${sec.cols || 12}`,
            ss.padding ? `padding:${ss.padding}px` : '',
            ss.bg ? `background:${ss.bg}` : '',
            ss.borderRadius ? `border-radius:${ss.borderRadius}px` : '',
            ss.borderColor ? `border:1px solid ${ss.borderColor}` : ''
          ].filter(Boolean).join(';');
          return `<div class="ms-layout-${sec.id}" style="${cellStyles}">${html}</div>`;
        }).join('');
        return cells ? `<div style="display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:8px">${cells}</div>` : '';
      }).join('');
    } else {
      // Legacy flat section order or default
      const sections = this.getCfg('sections', null);
      const orderedIds = sections
        ? sections.sort((a, b) => a.order - b.order).filter(s => s.enabled).map(s => s.id)
        : ['search_header', 'products', 'collections', 'pages', 'articles'];
      contentHtml = orderedIds.map(id => sectionMap[id] || '').join('');
    }

    // When layout manages filters placement, don't add the legacy filter column
    const showLegacyFilter = filterSidebarHtml && !layoutHasFilters;

    return `
      <style>@media(max-width:767px){.ms-filter-sidebar,.ms-layout-filters{display:none!important}.ms-results-col,.ms-layout-products{grid-column:span 12!important}#predictive-search-results-groups-wrapper{grid-template-columns:1fr!important}#predictive-search-results-products-list{grid-template-columns:repeat(2,1fr)!important}#predictive-search-results-products-list>li{grid-column:span 1!important}.ms-results-col>div,.ms-layout-products>div:not(#predictive-search-results-products-list){grid-template-columns:1fr!important}}</style>
      <div id="predictive-search-results" style="width:100%;${styleVars};background:var(--ms-overlay-bg,#fff)" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper" style="display:grid;grid-template-columns:repeat(12,minmax(0,1fr));background:var(--ms-container-bg,#fff)">
          ${showLegacyFilter ? `<div class="ms-filter-sidebar" style="border-right:1px solid #e5e7eb;padding:16px;overflow-y:auto;max-height:70vh;${filterColSpan}">${filterSidebarHtml}</div>` : ''}
          <div class="ms-results-col" style="${showLegacyFilter ? resultsColSpan : 'grid-column:span 12'}">
            ${noResultsHtml}
            ${contentHtml}
          </div>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${totalHits} ${this.t('results')}</span>
    `;
  }

  // ---- Filter sidebar ----

  buildFilterSidebarHTML(facetDistribution, facetStats) {
    if (!this.filtersConfig || !this.filtersConfig.facets) return '';
    const enabledFacets = this.filtersConfig.facets.filter(f => f.enabled);
    if (enabledFacets.length === 0) return '';

    const facetHtml = enabledFacets.map(facet => {
      if (facet.type === 'range' && facet.attribute === 'price') {
        return this.buildPriceRangeFilter(facet, facetStats);
      }
      if (facet.type === 'color') {
        return this.buildColorSwatchFilter(facet, facetDistribution);
      }
      return this.buildCheckboxFilter(facet, facetDistribution);
    }).join('');

    const hasActiveFilters = Object.keys(this.activeFilters).length > 0 || this.priceRange.min !== null || this.priceRange.max !== null;
    const resetBtn = hasActiveFilters ? `<button type="button" onclick="document.querySelector('predictive-search').clearAllFilters()" style="width:100%;padding:8px 12px;font-size:0.8rem;font-weight:600;background:#fff;color:#6b7280;border:1px solid #d1d5db;border-radius:6px;cursor:pointer;transition:all .15s;margin-top:4px">Nulstil</button>` : '';

    return facetHtml + resetBtn;
  }

  buildCheckboxFilter(facet, facetDistribution) {
    const values = facetDistribution?.[facet.attribute] || {};
    const activeValues = this.activeFilters[facet.attribute] || [];
    const cacheKey = `_facetCache_${facet.attribute}`;

    // Cache the original order (unfiltered) so it stays stable when filters are applied
    if (Object.keys(values).length > 0 && activeValues.length === 0) {
      const sorted = Object.entries(values).sort((a, b) => b[1] - a[1]);
      this[cacheKey] = sorted.map(([k]) => k);
    }

    // Build entries using cached order if available, updating counts from current distribution
    let entries;
    if (this[cacheKey]) {
      const countMap = { ...values };
      entries = this[cacheKey].map(k => [k, countMap[k] ?? 0]);
      // Add any new values not in cache
      for (const [k, v] of Object.entries(values)) {
        if (!this[cacheKey].includes(k)) entries.push([k, v]);
      }
    } else {
      entries = Object.entries(values).sort((a, b) => b[1] - a[1]);
    }

    // Always include currently-selected filter values even if they're not in facetDistribution
    const existingKeys = new Set(entries.map(([k]) => k));
    for (const val of activeValues) {
      if (!existingKeys.has(val)) entries.push([val, 0]);
    }

    if (entries.length === 0) return '';

    const isCollapsed = facet.collapsed && activeValues.length === 0;
    const showAll = entries.length <= 10;
    const visibleEntries = showAll ? entries : entries.slice(0, 10);
    const hiddenEntries = showAll ? [] : entries.slice(10);

    const buildFacetLabel = (count, checked, safeVal) => `<label class="meili-facet-option" style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:3px 4px;font-size:0.875rem;border-radius:4px">
        <input type="checkbox" ${checked} onchange="document.querySelector('predictive-search').onFilterChange('${facet.attribute}','${safeVal}',this.checked)" style="width:14px;height:14px;accent-color:#1a1a1a;flex-shrink:0;margin:0">
        <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${safeVal}</span>
        <span style="font-size:0.75rem;color:#9ca3af">${count}</span>
      </label>`;

    const checkboxes = visibleEntries.map(([val, count]) => {
      const checked = activeValues.includes(val) ? 'checked' : '';
      return buildFacetLabel(count, checked, this.escapeHtml(val));
    }).join('');

    const hiddenCheckboxes = hiddenEntries.map(([val, count]) => {
      const checked = activeValues.includes(val) ? 'checked' : '';
      return buildFacetLabel(count, checked, this.escapeHtml(val));
    }).join('');

    const showMoreHtml = hiddenEntries.length > 0 ? `
      <div class="meili-facet-hidden-${facet.attribute}" style="display:none">${hiddenCheckboxes}</div>
      <button type="button" onclick="this.previousElementSibling.style.display='block';this.style.display='none'" class="meili-show-more" style="font-size:0.75rem;color:#1a1a1a;margin-top:4px;background:none;border:none;cursor:pointer;padding:0">${this.t('show_more', {n: hiddenEntries.length})}</button>
    ` : '';

    return `
      <div style="margin-bottom:16px">
        <button type="button" onclick="var c=this.nextElementSibling;c.style.display=c.style.display==='none'?'':'none';var s=this.querySelector('svg');s.style.transform=s.style.transform?'':'rotate(180deg)'" style="display:flex;align-items:center;justify-content:space-between;width:100%;font-size:0.875rem;font-weight:600;color:#1f2937;margin-bottom:8px;background:none;border:none;cursor:pointer;padding:0">
          <span style="display:flex;align-items:center;gap:6px;flex:1">${this.escapeHtml(facet.label)}</span>
          <svg style="width:16px;height:16px;transition:transform .2s${isCollapsed ? ';transform:rotate(180deg)' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div style="${isCollapsed ? 'display:none' : ''}">
          ${checkboxes}
          ${showMoreHtml}
        </div>
      </div>
    `;
  }

  buildColorSwatchFilter(facet, facetDistribution) {
    const values = facetDistribution?.[facet.attribute] || {};
    const activeValues = this.activeFilters[facet.attribute] || [];
    const entries = Object.entries(values).sort((a, b) => b[1] - a[1]);
    // Include active values not in distribution
    const existingKeys = new Set(entries.map(([k]) => k));
    for (const val of activeValues) {
      if (!existingKeys.has(val)) entries.push([val, 0]);
    }
    if (entries.length === 0) return '';
    const swatches = this.colorSwatches || {};
    const isCollapsed = facet.collapsed && activeValues.length === 0;

    const swatchItems = entries.map(([val, count]) => {
      const s = swatches[val] || {};
      const displayName = s.name || val;
      const isActive = activeValues.includes(val);
      const safeVal = this.escapeHtml(val);
      const swatchVisual = s.image
        ? `<img src="${this.escapeHtml(s.image)}" alt="${safeVal}" style="width:28px;height:28px;border-radius:50%;object-fit:cover;border:2px solid ${isActive ? '#1a1a1a' : '#e5e7eb'};box-sizing:border-box">`
        : `<div style="width:28px;height:28px;border-radius:50%;background:${s.hex || '#ccc'};border:2px solid ${isActive ? '#1a1a1a' : '#e5e7eb'};box-sizing:border-box"></div>`;
      return `<button type="button" onclick="document.querySelector('predictive-search').onFilterChange('${facet.attribute}','${safeVal}',${!isActive})" title="${safeVal} (${count})" style="display:flex;flex-direction:column;align-items:center;gap:2px;cursor:pointer;background:none;border:none;padding:4px;border-radius:8px;transition:all .15s;${isActive ? 'background:#f3f4f6;' : ''}">
        ${swatchVisual}
        <span style="font-size:0.65rem;color:${isActive ? '#1a1a1a' : '#6b7280'};max-width:48px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:${isActive ? '600' : '400'}">${this.escapeHtml(displayName)}</span>
      </button>`;
    }).join('');

    return `
      <div style="margin-bottom:16px">
        <button type="button" onclick="var c=this.nextElementSibling;c.style.display=c.style.display==='none'?'':'none';var s=this.querySelector('svg');s.style.transform=s.style.transform?'':'rotate(180deg)'" style="display:flex;align-items:center;justify-content:space-between;width:100%;font-size:0.875rem;font-weight:600;color:#1f2937;margin-bottom:8px;background:none;border:none;cursor:pointer;padding:0">
          <span>${this.escapeHtml(facet.label)}</span>
          <svg style="width:16px;height:16px;transition:transform .2s${isCollapsed ? ';transform:rotate(180deg)' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div style="display:${isCollapsed ? 'none' : 'flex'};flex-wrap:wrap;gap:4px">
          ${swatchItems}
        </div>
      </div>
    `;
  }

  buildPriceRangeFilter(facet, facetStats) {
    const stats = facetStats?.price || {};
    // Cache the original unfiltered price range so the slider bounds stay fixed
    if (!this._priceRangeCache && stats.min != null) {
      this._priceRangeCache = { min: Math.floor(stats.min), max: Math.ceil(stats.max) };
    }
    const min = this._priceRangeCache?.min ?? (stats.min != null ? Math.floor(stats.min) : 0);
    const max = this._priceRangeCache?.max ?? (stats.max != null ? Math.ceil(stats.max) : 10000);
    const currentMin = this.priceRange.min !== null ? this.priceRange.min : '';
    const currentMax = this.priceRange.max !== null ? this.priceRange.max : '';
    const isCollapsed = facet.collapsed && this.priceRange.min === null && this.priceRange.max === null;
    const showSlider = facet.showSlider !== false;

    const sliderHtml = showSlider ? `
      <div class="meili-price-slider" style="position:relative;padding:8px 4px 16px;height:32px">
        <div style="position:absolute;left:4px;right:4px;top:14px;height:6px;background:#e5e7eb;border-radius:3px"></div>
        <div id="meili-price-track" style="position:absolute;top:14px;height:6px;background:#000;border-radius:3px;left:0%;right:0%"></div>
        <input type="range" id="meili-slider-min" min="${min}" max="${max}" value="${currentMin || min}" step="1"
          class="meili-range-thumb" style="position:absolute;width:100%;top:6px;-webkit-appearance:none;appearance:none;background:transparent;z-index:3"
          oninput="document.querySelector('predictive-search').onSliderInput('min')"
          onchange="document.querySelector('predictive-search').onSliderChange()">
        <input type="range" id="meili-slider-max" min="${min}" max="${max}" value="${currentMax || max}" step="1"
          class="meili-range-thumb" style="position:absolute;width:100%;top:6px;-webkit-appearance:none;appearance:none;background:transparent;z-index:4"
          oninput="document.querySelector('predictive-search').onSliderInput('max')"
          onchange="document.querySelector('predictive-search').onSliderChange()">
      </div>
    ` : '';

    return `
      <div style="margin-bottom:16px">
        <button type="button" onclick="var c=this.nextElementSibling;c.style.display=c.style.display==='none'?'':'none';var s=this.querySelector('svg');s.style.transform=s.style.transform?'':'rotate(180deg)'" style="display:flex;align-items:center;justify-content:space-between;width:100%;font-size:0.875rem;font-weight:600;color:#1f2937;margin-bottom:8px;background:none;border:none;cursor:pointer;padding:0">
          <span>${this.escapeHtml(facet.label)}</span>
          <svg style="width:16px;height:16px;transition:transform .2s${isCollapsed ? ';transform:rotate(180deg)' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div style="${isCollapsed ? 'display:none' : ''}">
          ${sliderHtml}
          <div style="display:flex;gap:8px;align-items:center">
            <input type="number" id="meili-price-min" placeholder="${min}" value="${currentMin}" min="${min}" max="${max}" class="meili-price-input" style="width:100%;padding:4px 8px;font-size:0.875rem;border:1px solid #d1d5db;border-radius:4px;outline:none;box-sizing:border-box">
            <span style="color:#9ca3af;font-size:0.875rem;flex-shrink:0">-</span>
            <input type="number" id="meili-price-max" placeholder="${max}" value="${currentMax}" min="${min}" max="${max}" class="meili-price-input" style="width:100%;padding:4px 8px;font-size:0.875rem;border:1px solid #d1d5db;border-radius:4px;outline:none;box-sizing:border-box">
          </div>
          <button type="button" onclick="document.querySelector('predictive-search').onPriceRangeApply()" class="meili-apply-btn" style="margin-top:8px;width:100%;padding:6px 12px;font-size:0.75rem;font-weight:500;background:#000;color:#fff;border:none;border-radius:4px;cursor:pointer;transition:background .15s">${this.t('apply')}</button>
        </div>
      </div>
    `;
  }

  // ---- Active filters chips ----

  buildActiveFiltersHTML() {
    const chips = [];

    for (const [attr, values] of Object.entries(this.activeFilters)) {
      const facetCfg = this.filtersConfig?.facets?.find(f => f.attribute === attr);
      const label = facetCfg?.label || attr;
      for (const val of values) {
        chips.push(`<button type="button" onclick="document.querySelector('predictive-search').removeFilter('${attr}','${this.escapeHtml(val)}')" class="meili-filter-chip" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f3f4f6;color:#1a1a1a;border-radius:9999px;font-size:0.75rem;font-weight:500;border:none;cursor:pointer;transition:background .15s">
          ${this.escapeHtml(label)}: ${this.escapeHtml(val)}
          <svg style="width:12px;height:12px" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>`);
      }
    }

    if (this.priceRange.min !== null || this.priceRange.max !== null) {
      const label = this.priceRange.min !== null && this.priceRange.max !== null
        ? `${this.formatPrice(this.priceRange.min)} - ${this.formatPrice(this.priceRange.max)}`
        : this.priceRange.min !== null ? this.t('from_price', {price: this.formatPrice(this.priceRange.min)}) : this.t('up_to_price', {price: this.formatPrice(this.priceRange.max)});
      chips.push(`<button type="button" onclick="document.querySelector('predictive-search').removePriceFilter()" class="meili-filter-chip" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f3f4f6;color:#1a1a1a;border-radius:9999px;font-size:0.75rem;font-weight:500;border:none;cursor:pointer;transition:background .15s">
        ${this.t('price_label')}: ${label}
        <svg style="width:12px;height:12px" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`);
    }

    if (chips.length === 0) return '';

    return `
      <div style="background:#fff;border-bottom:1px solid #e5e7eb;padding:8px 16px;display:flex;flex-wrap:wrap;gap:8px;align-items:center">
        ${chips.join('')}
        <button type="button" onclick="document.querySelector('predictive-search').clearAllFilters()" class="meili-clear-all" style="font-size:0.75rem;color:#6b7280;text-decoration:underline;margin-left:8px;background:none;border:none;cursor:pointer">${this.t('clear_all')}</button>
      </div>
    `;
  }

  // ---- Filter event handlers ----

  onFilterChange(attribute, value, checked) {
    this._filterInteraction = true;
    if (!this.activeFilters[attribute]) this.activeFilters[attribute] = [];
    if (checked) {
      if (!this.activeFilters[attribute].includes(value)) {
        this.activeFilters[attribute].push(value);
      }
    } else {
      this.activeFilters[attribute] = this.activeFilters[attribute].filter(v => v !== value);
      if (this.activeFilters[attribute].length === 0) delete this.activeFilters[attribute];
    }
    this.cachedResults = {};
    this.getSearchResults(this.searchTerm);
  }

  onPriceRangeApply() {
    this._filterInteraction = true;
    const minEl = this.querySelector('#meili-price-min') || document.getElementById('meili-price-min');
    const maxEl = this.querySelector('#meili-price-max') || document.getElementById('meili-price-max');
    const minVal = minEl?.value ? parseFloat(minEl.value) : null;
    const maxVal = maxEl?.value ? parseFloat(maxEl.value) : null;
    this.priceRange = { min: minVal, max: maxVal };
    this.cachedResults = {};
    this.getSearchResults(this.searchTerm);
  }

  removeFilter(attribute, value) {
    this._filterInteraction = true;
    if (this.activeFilters[attribute]) {
      this.activeFilters[attribute] = this.activeFilters[attribute].filter(v => v !== value);
      if (this.activeFilters[attribute].length === 0) delete this.activeFilters[attribute];
    }
    this.cachedResults = {};
    this.getSearchResults(this.searchTerm);
  }

  removePriceFilter() {
    this._filterInteraction = true;
    this.priceRange = { min: null, max: null };
    this._priceRangeCache = null;
    this.cachedResults = {};
    this.getSearchResults(this.searchTerm);
  }

  clearAllFilters() {
    this._filterInteraction = true;
    this.activeFilters = {};
    this.priceRange = { min: null, max: null };
    this._priceRangeCache = null;
    this.cachedResults = {};
    this.getSearchResults(this.searchTerm);
  }

  // ---- Slider handlers ----

  onSliderInput(which) {
    this._resultsInteraction = true;
    const minSlider = this.querySelector('#meili-slider-min') || document.getElementById('meili-slider-min');
    const maxSlider = this.querySelector('#meili-slider-max') || document.getElementById('meili-slider-max');
    const minInput = this.querySelector('#meili-price-min') || document.getElementById('meili-price-min');
    const maxInput = this.querySelector('#meili-price-max') || document.getElementById('meili-price-max');
    if (!minSlider || !maxSlider) return;

    let minVal = parseInt(minSlider.value);
    let maxVal = parseInt(maxSlider.value);

    // Prevent crossing
    if (which === 'min' && minVal > maxVal) { minSlider.value = maxVal; minVal = maxVal; }
    if (which === 'max' && maxVal < minVal) { maxSlider.value = minVal; maxVal = minVal; }

    // Swap z-index so the last-dragged thumb stays on top
    if (which === 'min') {
      minSlider.style.zIndex = '5';
      maxSlider.style.zIndex = '4';
    } else {
      minSlider.style.zIndex = '3';
      maxSlider.style.zIndex = '4';
    }

    // Sync number inputs
    if (minInput) minInput.value = minVal;
    if (maxInput) maxInput.value = maxVal;

    // Update track highlight
    this.updateSliderTrack(minSlider, maxSlider);
  }

  onSliderChange() {
    this._resultsInteraction = false;
    this.onPriceRangeApply();
  }

  updateSliderTrack(minSlider, maxSlider) {
    const track = this.querySelector('#meili-price-track') || document.getElementById('meili-price-track');
    if (!track || !minSlider || !maxSlider) return;
    const rangeMin = parseInt(minSlider.min);
    const rangeMax = parseInt(minSlider.max);
    const span = rangeMax - rangeMin || 1;
    const leftPct = ((parseInt(minSlider.value) - rangeMin) / span) * 100;
    const rightPct = ((rangeMax - parseInt(maxSlider.value)) / span) * 100;
    track.style.left = leftPct + '%';
    track.style.right = rightPct + '%';
  }

  // ---- Translations & Language ----

  t(key, replacements = {}) {
    // Dashboard overrides take priority, then hardcoded defaults
    const dashTrans = this.dashboardTranslations?.[this.locale];
    const str = dashTrans?.[key] || MEILI_TRANSLATIONS[this.locale]?.[key] || MEILI_TRANSLATIONS['da']?.[key] || key;
    return Object.entries(replacements).reduce((s, [k, v]) => s.replace(`{${k}}`, v), str);
  }

  // ---- Utilities ----

  formatPrice(price) {
    const localeMap = { da: 'da-DK', en: 'en-GB', de: 'de-DE', sv: 'sv-SE', no: 'nb-NO', fi: 'fi-FI' };
    return new Intl.NumberFormat(localeMap[this.locale] || 'da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    this._initBundleSliders();
    if (this._filterInteraction) {
      this._filterInteraction = false;
      this.input.focus();
    }
  }

  _initBundleSliders() {
    if (typeof Splide === 'undefined') return;
    this.predictiveSearchResults.querySelectorAll('.splide:not(.is-initialized)').forEach(slider => {
      try {
        new Splide(slider, {
          type: 'slide',
          autoWidth: true,
          pagination: false,
          arrows: true,
          padding: { right: '3px', left: '3px' },
          gap: '3px',
          drag: 'free',
          snap: false,
          omitEnd: false,
          speed: 400,
          start: 0
        }).mount();
      } catch (e) { /* skip broken sliders */ }
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this._applyContainerStyles();
  }

  _applyContainerStyles() {
    const width = this.getCfg('containerWidth', '100%');
    const direction = this.getCfg('slideDirection', 'none');
    const el = this.predictiveSearchResults;
    if (!el) return;

    // Apply width — use left/right for absolute positioning
    if (width && width !== '100%') {
      el.style.width = width;
      if (direction === 'right') {
        el.style.left = 'auto';
        el.style.right = '0';
      } else if (direction === 'left') {
        el.style.left = '0';
        el.style.right = 'auto';
      } else {
        el.style.left = '0';
        el.style.right = '0';
        el.style.margin = '0 auto';
      }
    }

    // Apply slide-in animation
    if (direction !== 'none' && !el.dataset.slideApplied) {
      el.dataset.slideApplied = '1';
      var from = direction === 'left' ? '-100%' : '100%';
      el.style.animation = 'none';
      el.offsetHeight; // force reflow
      if (!document.getElementById('meili-slide-keyframes')) {
        var style = document.createElement('style');
        style.id = 'meili-slide-keyframes';
        style.textContent = '@keyframes meiliSlideIn{from{transform:translateX(var(--meili-slide-from));opacity:0.5}to{transform:translateX(0);opacity:1}}';
        document.head.appendChild(style);
      }
      el.style.setProperty('--meili-slide-from', from);
      el.style.animation = 'meiliSlideIn .3s ease-out forwards';
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
    delete this.predictiveSearchResults.dataset.slideApplied;
  }

  // ---- Default products (empty search state) ----

  _resolveDefaultProductsConfig() {
    // Check products section styles for defaultProducts first, fall back to global
    let dp = this.getCfg('defaultProducts', null);
    const layout = this.getCfg('layout', null);
    if (layout && Array.isArray(layout)) {
      for (const row of layout) {
        const prodSec = row.sections?.find(s => s.id === 'products');
        if (prodSec?.styles?.defaultProducts) { dp = prodSec.styles.defaultProducts; break; }
      }
    }
    if (!dp || !dp.enabled) return null;
    // Apply hardcoded defaults (title, source, maxItems are no longer configurable)
    return {
      enabled: true,
      title: this.t('recommended'),
      source: 'recently_viewed',
      maxItems: 8,
      sources: [
        { type: 'recently_viewed', enabled: true },
        { type: 'popular', enabled: true }
      ]
    };
  }

  async showDefaultProducts() {
    await this.loadSearchConfig();
    const dp = this._resolveDefaultProductsConfig();
    const fullOnOpen = this._isFullOnOpenActive();
    if (!dp) {
      if (!fullOnOpen) this.close(true);
      return;
    }

    try {
      const { hits, facetDistribution, facetStats } = await this._fetchCombinedDefaultProducts(dp);

      if (hits.length === 0) { if (!fullOnOpen) this.close(true); return; }
      const defaultSnippetCards = await this.fetchSnippetCards(hits.map(h => h.handle));
      const markup = this.buildDefaultProductsHTML(hits, dp.title, defaultSnippetCards, facetDistribution, facetStats);
      this.renderSearchResults(markup);
    } catch (e) {
      console.warn('[Meilisearch] Default products error:', e.message);
      if (!fullOnOpen) this.close(true);
    }
  }

  buildDefaultProductsHTML(hits, title, snippetCards, facetDistribution, facetStats) {
    // Reuse the main search results renderer for consistent layout and styling
    // Place the title above the product grid (not as a full-width search_header)
    const productsTitle = `
      <div style="padding:12px 16px 4px">
        <span style="font-weight:600;color:var(--ms-text-secondary,#6b7280)">${this.escapeHtml(title)}</span>
      </div>`;
    return this.buildMeilisearchResultsHTML(hits, '', hits.length, facetDistribution || null, facetStats || null, snippetCards, { headerHtml: '', productsTitle });
  }
}

customElements.define('predictive-search', MeilisearchSearch);

// ---- Recently viewed product tracker ----
(function() {
  if (!window.location.pathname.startsWith('/products/')) return;
  const handle = window.location.pathname.split('/products/')[1]?.split('?')[0]?.split('#')[0];
  if (!handle) return;
  try {
    let viewed = JSON.parse(localStorage.getItem('smelt_recently_viewed') || '[]');
    viewed = viewed.filter(h => h !== handle);
    viewed.unshift(handle);
    if (viewed.length > 20) viewed = viewed.slice(0, 20);
    localStorage.setItem('smelt_recently_viewed', JSON.stringify(viewed));
  } catch (e) { /* localStorage unavailable */ }
})();
